import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, FolderOpen, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { PlatformIcon } from '@/components/PlatformIcon';
import { ApiError, type RepeatSchedule, type ScheduleRun } from '../api/types';
import { useRunAdded, useScheduleRuns } from '../hooks/schedules';
import { profileUrl } from '../lib/platform';
import { OutreachStatusBadge, doneReasonLabel } from '../components/OutreachStatus';
import { formatCount } from '../components/lead-table/rows';
import { useDiscoveryPaths } from '../workspace/paths';
import { Note } from '../workspace/ui';
import { HealthPill, RunStatusPill } from './HealthPill';
import { cadenceLabel, count, countryBreakdownLabel, formatDay, formatDuration, formatRunDate } from './model';
import { ChangesList, OutsideTargetBadge, SettingsDisclosure } from './ScheduleSettings';

interface Props {
  /** The schedule whose history is open, or null when closed. */
  publicId: string | null;
  /** The row from the list, shown at once while the history loads. */
  fallback: RepeatSchedule | null;
  /** The run whose added creators are open, if any. */
  runJobId: string | null;
  onRun: (jobId: string | null) => void;
  onClose: () => void;
}

/**
 * One schedule's run history: what each run scanned and what became of it —
 * added, already known, filtered out, failed. Each "added" count opens the
 * creators that run put in the folder.
 */
export function ScheduleRunsSheet({ publicId, fallback, runJobId, onRun, onClose }: Props): JSX.Element {
  const paths = useDiscoveryPaths();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useScheduleRuns(publicId, page);
  const schedule = data?.schedule ?? fallback;
  const openRun = runJobId ? data?.runs.find((r) => r.jobId === runJobId) ?? null : null;

  return (
    <Sheet
      open={!!publicId}
      onOpenChange={(open) => {
        if (!open) {
          setPage(1);
          onClose();
        }
      }}
    >
      <SheetContent aria-describedby="repeat-run-history-desc">
        <div className="border-b border-border px-5 pb-4 pt-5 pr-12">
          <SheetTitle className="flex items-center gap-2 text-lg font-semibold">
            <PlatformIcon platform={schedule?.platform ?? 'tiktok'} size={16} className="shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate">{schedule?.name ?? 'Run history'}</span>
          </SheetTitle>
          <SheetDescription id="repeat-run-history-desc" asChild>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-muted-foreground">
              {schedule && <HealthPill schedule={schedule} />}
              {schedule && <span>{cadenceLabel(schedule.everyDays)}</span>}
              {schedule && (
                <span className="tabular-nums">
                  {schedule.runCount.toLocaleString()} run{schedule.runCount === 1 ? '' : 's'} · {schedule.totalAdded.toLocaleString()} added in total
                </span>
              )}
              {schedule?.folder && (
                <Link to={paths.folder(schedule.folder.publicId, 'leads')} className="inline-flex items-center gap-1 font-medium text-foreground hover:underline">
                  <FolderOpen className="h-3.5 w-3.5" />
                  {schedule.folder.name}
                </Link>
              )}
            </div>
          </SheetDescription>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {runJobId ? (
            <AddedCreators jobId={runJobId} run={openRun} onBack={() => onRun(null)} />
          ) : (
            <>
              {schedule && schedule.paramsSummary.length > 0 && (
                <div className="space-y-1.5">
                  <SettingsDisclosure summary={schedule.paramsSummary} label="What this schedule runs with" defaultOpen />
                  {schedule.paramsUpdatedAt && (
                    <p className="text-xs text-muted-foreground" data-settings-changed>
                      Settings changed {formatDay(schedule.paramsUpdatedAt)}
                      {schedule.paramsUpdatedByName ? ` by ${schedule.paramsUpdatedByName}` : ''}. Runs before that used
                      the previous settings.
                    </p>
                  )}
                  <OutsideTargetBadge count={schedule.outsideTargetTotal} />
                </div>
              )}
              {schedule && !schedule.historyComplete && (
                <Note tone="info">
                  Run history starts {formatDay(schedule.historySince)}. Runs before that weren't recorded one by one, so
                  they are counted in "{schedule.runCount.toLocaleString()} runs" but not listed here.
                </Note>
              )}
              {error && (
                <Note tone="error" role="alert">
                  {error instanceof ApiError && error.status === 404
                    ? "Run history isn't available for this schedule. It may have been deleted, or the Discovery service is still updating."
                    : `Couldn't load the run history: ${(error as Error).message}`}
                </Note>
              )}
              {isLoading && <p className="text-sm text-muted-foreground">Loading run history…</p>}
              {data && data.runs.length === 0 && (
                <EmptyState
                  icon={<History className="h-6 w-6" />}
                  title="No runs recorded yet"
                  hint={
                    schedule?.enabled
                      ? 'The first run starts within the hour after a schedule is created. It will be listed here when it does.'
                      : 'This schedule is paused. Resume it to start running again.'
                  }
                />
              )}
              {data && data.runs.length > 0 && (
                <>
                  <RunsTable runs={data.runs} onOpenAdded={(jobId) => onRun(jobId)} />
                  <Pagination page={data.page} limit={data.pageSize} total={data.total} onPageChange={setPage} />
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Scanned is every creator a run reached a verdict on. Already known means the team had them before the
                    run, so nothing was spent on them. Filtered out didn't match the search's rules. Failed means their
                    profile couldn't be loaded.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function RunsTable({ runs, onOpenAdded }: { runs: ScheduleRun[]; onOpenAdded: (jobId: string) => void }): JSX.Element {
  return (
    <div className="table-frame overflow-x-auto">
      <table className="w-full text-sm" aria-label="Run history">
        <thead>
          <tr className="text-left text-xs font-semibold text-muted-foreground/80">
            <th className="py-2 pr-3 font-semibold">Run</th>
            <th className="py-2 pr-3 font-semibold">Duration</th>
            <th className="py-2 pr-3 font-semibold">Status</th>
            <th className="py-2 pr-3 text-right font-semibold">Scanned</th>
            <th className="py-2 pr-3 text-right font-semibold">Added</th>
            <th className="py-2 pr-3 text-right font-semibold">Already known</th>
            <th className="py-2 pr-3 text-right font-semibold">Filtered out</th>
            <th className="py-2 text-right font-semibold">Failed</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.jobId} className="border-t border-border align-top transition-colors hover:bg-accent/40" data-run={r.jobId}>
              <td className="whitespace-nowrap py-2.5 pr-3">
                {formatRunDate(r.runAt)}
                {r.settingsDiffer && (
                  <p
                    className="text-xs text-pending-strong"
                    title="This run happened before the schedule's settings were last changed. Open its added creators to see what it ran with."
                    data-settings-differ
                  >
                    Ran with earlier settings
                  </p>
                )}
              </td>
              <td className="whitespace-nowrap py-2.5 pr-3 text-muted-foreground">
                {r.status === 'running' ? 'In progress' : formatDuration(r.durationMs)}
              </td>
              <td className="py-2.5 pr-3">
                <RunStatusPill status={r.status} />
                {r.error && <p className="mt-1 max-w-[28ch] text-xs text-destructive-strong">{r.error}</p>}
              </td>
              <td className="py-2.5 pr-3 text-right">{count(r.scanned)}</td>
              <td className="py-2.5 pr-3 text-right">
                {r.added > 0 ? (
                  <button
                    type="button"
                    onClick={() => onOpenAdded(r.jobId)}
                    className="rounded font-semibold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Show the ${r.added} creators added on ${formatRunDate(r.runAt)}`}
                  >
                    +{r.added.toLocaleString()}
                  </button>
                ) : (
                  <span className="text-muted-foreground">0</span>
                )}
                {r.countries.length > 0 && (
                  <p className="whitespace-nowrap text-xs font-normal text-muted-foreground" data-country-breakdown>
                    {countryBreakdownLabel(r.countries)}
                  </p>
                )}
                <OutsideTargetBadge count={r.outsideTargetCount} className="mt-1" />
              </td>
              <td className="py-2.5 pr-3 text-right">{count(r.skippedKnown)}</td>
              <td className="py-2.5 pr-3 text-right">{count(r.filteredOut)}</td>
              <td className="py-2.5 text-right">{count(r.failed)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Exactly who one run added, with where each of them stands now. */
function AddedCreators({ jobId, run, onBack }: { jobId: string; run: ScheduleRun | null; onBack: () => void }): JSX.Element {
  const paths = useDiscoveryPaths();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useRunAdded(jobId, page);
  const runAt = data?.runAt ?? run?.runAt ?? null;
  const added = data?.added ?? run?.added ?? null;
  const missing = data && added != null ? Math.max(0, added - data.total) : 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" />
          All runs
        </Button>
        <h3 className="text-sm font-semibold">
          Added by the run on {formatRunDate(runAt)}
          {data && <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">({data.total.toLocaleString()})</span>}
        </h3>
        {data?.folder && (
          <Button asChild variant="outline" size="sm" className="ml-auto">
            <Link to={paths.folder(data.folder.publicId, 'leads')}>
              <FolderOpen className="h-3.5 w-3.5" />
              Open in {data.folder.name}
            </Link>
          </Button>
        )}
      </div>

      {error && (
        <Note tone="error" role="alert">
          {error instanceof ApiError && error.status === 404
            ? "This run's creators can't be listed. The run may have been removed, or the server is still updating."
            : `Couldn't load the creators: ${(error as Error).message}`}
        </Note>
      )}
      {isLoading && <p className="text-sm text-muted-foreground">Loading creators…</p>}
      {data && data.targetCountries && (
        <p className="whitespace-normal text-xs leading-relaxed text-muted-foreground" data-target-countries>
          {data.targetCountries.length === 0
            ? "This run didn't filter on the creator's country."
            : `This run only kept creators based in: ${data.targetCountries.join(', ')}.`}
          {data.countries.length > 0 && ` Added: ${countryBreakdownLabel(data.countries, 8)}.`}
        </p>
      )}
      {data && <OutsideTargetBadge count={data.outsideTargetCount} />}
      {data && data.settingsDiffer && (
        <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/[0.08] px-3 py-2.5" data-run-settings-differ>
          <p className="text-sm font-medium">This run used earlier settings</p>
          <p className="text-xs text-muted-foreground">
            What changed between this run and the schedule's current settings:
          </p>
          <ChangesList changes={data.settingsChanges} />
        </div>
      )}
      {data && data.paramsSummary.length > 0 && (
        <SettingsDisclosure summary={data.paramsSummary} label="What this run ran with" />
      )}
      {data && missing > 0 && (
        <Note tone="info">
          The run counted {added!.toLocaleString()} added; {data.total.toLocaleString()} are listed. The other{' '}
          {missing.toLocaleString()} {missing === 1 ? 'was' : 'were'} removed from the folder since, or saved by another
          run at the same moment.
        </Note>
      )}
      {data && data.creators.length === 0 && !error && (
        <EmptyState title="None of this run's creators are in the folder now" hint="They were removed from the folder after the run added them." />
      )}
      {data && data.creators.length > 0 && (
        <>
          <div className="table-frame overflow-x-auto">
            <table className="w-full text-sm" aria-label="Creators added by this run">
              <thead>
                <tr className="text-left text-xs font-semibold text-muted-foreground/80">
                  <th className="py-2 pr-3 font-semibold">Creator</th>
                  <th className="py-2 pr-3 font-semibold">Country</th>
                  <th className="py-2 pr-3 text-right font-semibold">Followers</th>
                  <th className="py-2 pr-3 text-right font-semibold">Avg views</th>
                  <th className="py-2 font-semibold">Status now</th>
                </tr>
              </thead>
              <tbody>
                {data.creators.map((c) => {
                  const done = doneReasonLabel(c.doneReason);
                  return (
                    <tr key={`${c.platform}:${c.username}`} className="border-t border-border transition-colors hover:bg-accent/40">
                      <td className="py-2.5 pr-3">
                        <a
                          href={profileUrl(c.platform, c.username)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex max-w-full items-center gap-1.5 font-medium hover:underline"
                        >
                          <PlatformIcon platform={c.platform} size={14} className="shrink-0 text-muted-foreground" />
                          <span className="truncate">@{c.username}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </a>
                        {c.nickname && c.nickname !== c.username && (
                          <p className="truncate pl-5 text-xs text-muted-foreground">{c.nickname}</p>
                        )}
                      </td>
                      <td className="py-2.5 pr-3" data-creator-country>
                        {c.country ? (
                          <span className="whitespace-nowrap">
                            {c.country.name} <span className="text-xs text-muted-foreground">{c.country.code}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground" title="The creator's country isn't recorded. A run that filters on country normally drops these.">
                            Unknown
                          </span>
                        )}
                        {c.outsideTarget && (
                          <p className="mt-1">
                            <span className="inline-flex items-center rounded-md border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-xs font-semibold text-destructive-strong">
                              Outside target
                            </span>
                          </p>
                        )}
                        {c.contentLanguage && (
                          <p className="text-xs text-muted-foreground">Posts in {c.contentLanguage.toUpperCase()}</p>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right">{formatCount(c.followers)}</td>
                      <td className="py-2.5 pr-3 text-right">{formatCount(c.avgViews)}</td>
                      <td className="py-2.5">
                        <OutreachStatusBadge creator={c} />
                        {done && c.doneReason !== 'logged' && c.doneReason !== 'joined' && c.doneReason !== 'dismissed' && c.doneReason !== 'suppressed' && (
                          <p className="mt-1 text-xs text-muted-foreground">{done}</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} limit={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
